// Eskom PARCC — the Audit Trail export (PURE: no XLSX, no DOM).
//
// Turns the filtered audit rows into a flat header+data sheet for
// `workbookFromSheet` → `downloadWorkbook`. Every cell is passed through
// `neutraliseCell`: audit notes and field values carry user-entered text, so
// CSV/formula-injection safety is not optional. The timestamp is sliced from the
// ISO string (deterministic, no locale/timezone drift) rather than toLocaleString,
// so the builder stays a pure, testable data transform.
import { neutraliseCell } from './neutraliseCell';
import { actionLabel, surfaceLabel } from '../audit/auditActions';
import type { AuditLogEntry } from '../../lib/db';

export const AUDIT_TRAIL_HEADER = [
  'When (UTC)',
  'By',
  'Action',
  'Surface',
  'Reference',
  'What changed',
] as const;

/** "2026-08-20 10:00" from an ISO timestamp; the raw value if it is not ISO. */
function whenCell(iso: string): string {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(iso) ? `${iso.slice(0, 10)} ${iso.slice(11, 16)}` : iso;
}

/** The from→to changes joined, or the note when an entry carried no field diff. */
export function whatChanged(entry: AuditLogEntry): string {
  if (entry.changes.length > 0) {
    return entry.changes.map((c) => `${c.label}: ${c.from} → ${c.to}`).join('; ');
  }
  return entry.note ?? '';
}

/** Build the Audit Trail worksheet rows (row 0 = header), every cell neutralised. */
export function buildAuditTrailSheet(rows: readonly AuditLogEntry[]): string[][] {
  const body = rows.map((r) =>
    [
      whenCell(r.at),
      r.actorEmail ?? '',
      actionLabel(r.action),
      surfaceLabel(r.action),
      r.entityId ?? '',
      whatChanged(r),
    ].map(neutraliseCell),
  );
  return [AUDIT_TRAIL_HEADER.map((h) => neutraliseCell(h)), ...body];
}
